import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelection } from '../../context/SelectionContext';
import { useCourses } from '../../hooks/useCourses';
import { useChildren } from '../../hooks/useChildren';
import ResearchCourseCard from './ResearchCourseCard';
import { SkeletonResearchPage } from '../common/Skeleton';
import FadeTransition from '../common/FadeTransition';
import EmptyState from '../common/EmptyState';
import api from '../../utils/api';
import { Bug, FlaskConical, Trophy, CheckCircle, ChevronDown, AlertCircle, Calendar } from 'lucide-react';
import { SkeletonResearchCard } from '../common/Skeleton';

// Dev mode flag - shows all UI states for testing
const DEV_MODE = import.meta.env.VITE_DEV_MODE === 'true';

// Dev mode component to show all research/competition card states
function DevModePreview() {
  const { i18n } = useTranslation();
  const isKorean = i18n.language === 'ko';

  return (
    <div className="border-2 border-dashed border-purple-400 dark:border-purple-600 rounded-xl p-4 mb-6">
      <div className="flex items-center gap-2 mb-4">
        <Bug className="w-5 h-5 text-purple-600 dark:text-purple-400" />
        <span className="text-sm font-bold text-purple-700 dark:text-purple-300">
          DEV MODE: {isKorean ? '연구/대회 UI 상태' : 'Research & Competition UI States'}
        </span>
      </div>

      {/* State 1: Loading (Skeleton) */}
      <div className="mb-4">
        <p className="text-xs font-semibold text-purple-600 dark:text-purple-400 mb-2">
          1. {isKorean ? '로딩 상태 (스켈레톤)' : 'Loading State (Skeleton)'}
        </p>
        <div className="space-y-3">
          <SkeletonResearchCard />
          <SkeletonResearchCard />
        </div>
      </div>

      {/* State 2: Empty State */}
      <div className="mb-4">
        <p className="text-xs font-semibold text-purple-600 dark:text-purple-400 mb-2">
          2. {isKorean ? '빈 상태' : 'Empty State'}
        </p>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 text-center">
          <p className="text-gray-500 dark:text-gray-400">
            {isKorean ? '연구 또는 대회 강좌가 없습니다.' : 'No research or competition courses found.'}
          </p>
        </div>
      </div>

      {/* State 3: Incomplete Count Banner */}
      <div className="mb-4">
        <p className="text-xs font-semibold text-purple-600 dark:text-purple-400 mb-2">
          3. {isKorean ? '미완료 개수 배너' : 'Incomplete Count Banner'}
        </p>
        <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            {isKorean
              ? '3개의 강좌에 정보 입력이 필요합니다. 아래에서 필요한 정보를 입력해 주세요.'
              : '3 courses need your input. Please fill in the required information below.'}
          </p>
        </div>
      </div>

      {/* State 4: Research Card - Collapsed (Needs Input) */}
      <div className="mb-4">
        <p className="text-xs font-semibold text-purple-600 dark:text-purple-400 mb-2">
          4. {isKorean ? '연구 카드 - 접힘 (입력 필요)' : 'Research Card - Collapsed (Needs Input)'}
        </p>
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
          <div className="p-3 sm:p-5">
            <div className="flex items-center justify-between gap-2 sm:gap-4">
              <div className="flex items-center gap-2.5 sm:gap-4 min-w-0 flex-1">
                <div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: '#6366f115', color: '#6366f1' }}>
                  <FlaskConical className="w-4 h-4 sm:w-5 sm:h-5" strokeWidth={1.5} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
                    <h3 className="font-semibold text-gray-900 dark:text-white text-sm sm:text-base truncate">
                      {isKorean ? '모의 연구 프로젝트' : 'Mock Research Project'}
                    </h3>
                    <span className="px-1.5 py-0.5 text-[10px] font-medium rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400">
                      {isKorean ? '입력 필요' : 'Needs Input'}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center hover:bg-gray-50 dark:hover:bg-gray-700">
                <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Transform backend course data to the format expected by cards
const transformCourse = (course) => ({
  id: course.id,
  code: course.code,
  title: course.course_title,
  subtitle: course.subject || '',
  startDate: course.start_date,
  displayName: course.display_name,
});

// Group courses by display_name (courses with same display_name are grouped together)
const groupCoursesByDisplayName = (courses, incompleteCourseIds) => {
  const grouped = {};
  const ungrouped = [];

  courses.forEach(course => {
    if (course.displayName) {
      if (!grouped[course.displayName]) {
        grouped[course.displayName] = {
          displayName: course.displayName,
          courses: [],
          needsInput: false,
        };
      }
      grouped[course.displayName].courses.push(course);
      // If any course in group needs input, the group needs input
      if (course.needsInput) {
        grouped[course.displayName].needsInput = true;
      }
    } else {
      // No display_name - show individually
      ungrouped.push(course);
    }
  });

  // Convert grouped object to array with computed properties
  const groupedArray = Object.values(grouped).map(group => ({
    ...group,
    // Use displayName as title for the card
    title: group.displayName,
    // For sorting: use earliest start date from any course in group
    startDate: group.courses.reduce((earliest, c) => {
      if (!c.startDate) return earliest;
      if (!earliest) return c.startDate;
      return new Date(c.startDate) < new Date(earliest) ? c.startDate : earliest;
    }, null),
    // For incomplete check: check if any course id in group is incomplete
    id: `group-${group.displayName}`,
    ids: group.courses.map(c => c.id),
    isGroup: true,
    // Check if any course in the group is incomplete
    isIncomplete: group.courses.some(c => c.needsInput && incompleteCourseIds.includes(c.id)),
  }));

  return { groupedArray, ungrouped };
};

// Sort helper: uninput courses first, then by most recent start date
const sortCourses = (items, incompleteCourseIds) => {
  return [...items].sort((a, b) => {
    // For groups, check if any course in group is incomplete
    const aIsUninput = a.isGroup
      ? a.isIncomplete
      : (a.needsInput && incompleteCourseIds.includes(a.id));
    const bIsUninput = b.isGroup
      ? b.isIncomplete
      : (b.needsInput && incompleteCourseIds.includes(b.id));

    if (aIsUninput && !bIsUninput) return -1;
    if (!aIsUninput && bIsUninput) return 1;

    // Second priority: most recent start date first
    const aDate = a.startDate ? new Date(a.startDate) : new Date(0);
    const bDate = b.startDate ? new Date(b.startDate) : new Date(0);
    return bDate - aDate;
  });
};

export default function ResearchCourseList() {
  const { t } = useTranslation('research');
  const { selectedChild } = useSelection();
  const { children } = useChildren();

  // Only fetch courses if selected child is valid (belongs to this parent)
  const isValidChild = selectedChild && children.some(c => c.id === selectedChild.id);
  const { courses: allCourses, loading } = useCourses(isValidChild ? selectedChild?.id : null);
  const [incompleteCount, setIncompleteCount] = useState(0);
  const [incompleteCourseIds, setIncompleteCourseIds] = useState([]);


  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchIncompleteData = useCallback(async () => {
    if (!isValidChild || !selectedChild?.id) return;
    try {
      const response = await api.get(`/parent/children/${selectedChild.id}/competition-details/incomplete-count`);
      setIncompleteCount(response.data.incomplete_count || 0);
      setIncompleteCourseIds(response.data.incomplete_course_ids || []);
    } catch (err) {
      console.error('Failed to fetch incomplete data:', err);
    }
  }, [isValidChild, selectedChild?.id]);

  useEffect(() => {
    fetchIncompleteData();
  }, [fetchIncompleteData]);

  if (!selectedChild) {
    return null;
  }

  // Filter for all competition/research courses
  const courses = allCourses
    .filter(c => c.is_competition_research)
    .map(c => ({
      ...transformCourse(c),
      needsInput: c.needs_parent_input,
    }));

  // Group courses by display_name
  const { groupedArray, ungrouped } = groupCoursesByDisplayName(courses, incompleteCourseIds);

  // Separate ungrouped into Research (R-) and Competition (others)
  const ungroupedResearch = ungrouped.filter(c => c.code?.startsWith('R-'));
  const ungroupedCompetition = ungrouped.filter(c => !c.code?.startsWith('R-'));

  // Separate grouped by whether first course is Research or Competition
  const groupedResearch = groupedArray.filter(g => g.courses[0]?.code?.startsWith('R-'));
  const groupedCompetition = groupedArray.filter(g => !g.courses[0]?.code?.startsWith('R-'));

  // Combine and sort: groups + ungrouped individuals
  const researchCourses = sortCourses(
    [...groupedResearch, ...ungroupedResearch],
    incompleteCourseIds
  );
  const competitionCourses = sortCourses(
    [...groupedCompetition, ...ungroupedCompetition],
    incompleteCourseIds
  );

  const hasAnyCourses = courses.length > 0;

  // Determine state for smooth transitions
  const getStateKey = () => {
    if (loading) return 'loading';
    if (!hasAnyCourses) return 'empty';
    return 'content';
  };

  // Unified skeleton for loading state - matches Research page layout
  const loadingSkeleton = (
    <div className="px-4 py-3 sm:px-6 sm:py-4 md:px-12 lg:px-20">
      <SkeletonResearchPage />
    </div>
  );

  const renderContent = () => {
    if (loading) {
      return loadingSkeleton;
    }

    if (!hasAnyCourses) {
      return (
        <div className="px-4 py-3 sm:px-6 sm:py-4 md:px-12 lg:px-20">
          <EmptyState message={t('list.empty')} />
        </div>
      );
    }

    return (
      <div className="px-4 py-3 sm:px-6 sm:py-4 md:px-12 lg:px-20">
        {/* Dev Mode Preview */}
        {DEV_MODE && <DevModePreview />}

        {/* Incomplete Count Banner */}
        {incompleteCount > 0 && (
          <div className="mb-4 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              {t('list.incompleteCount', { count: incompleteCount })}
            </p>
          </div>
        )}

        {/* Research Courses (R- prefix) - Purple theme */}
        {researchCourses.length > 0 && (
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-indigo-600 dark:text-indigo-400 uppercase tracking-wider mb-3 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-indigo-500" />
              {t('list.research')}
            </h3>
            {researchCourses.map((item) => (
              <ResearchCourseCard
                key={item.id}
                course={item.isGroup ? item.courses[0] : item}
                groupedCourses={item.isGroup ? item.courses : null}
                displayName={item.isGroup ? item.displayName : null}
                initialSubmitted={item.isGroup
                  ? !item.isIncomplete
                  : (item.needsInput && !incompleteCourseIds.includes(item.id))}
                onSubmissionChange={fetchIncompleteData}
              />
            ))}
          </div>
        )}

        {/* Competition Courses (non R-) - Yellow/Amber theme */}
        {competitionCourses.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold text-amber-600 dark:text-amber-400 uppercase tracking-wider mb-3 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-amber-500" />
              {t('list.competition')}
            </h3>
            {competitionCourses.map((item) => (
              <ResearchCourseCard
                key={item.id}
                course={item.isGroup ? item.courses[0] : item}
                groupedCourses={item.isGroup ? item.courses : null}
                displayName={item.isGroup ? item.displayName : null}
                initialSubmitted={item.isGroup
                  ? !item.isIncomplete
                  : (item.needsInput && !incompleteCourseIds.includes(item.id))}
                onSubmissionChange={fetchIncompleteData}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  // Build transition key for smooth transitions
  const stateKey = getStateKey();
  const transitionKey = `${selectedChild?.id}-${stateKey}`;

  return (
    <FadeTransition transitionKey={transitionKey}>
      {renderContent()}
    </FadeTransition>
  );
}
